/**
 * Counting a displayed text's numbers from one value to the next, for the Mira tiles (Taimoor,
 * 2026-09-30: "a transition animation on the numbers instead of just changing the number").
 *
 * Works on the finished, translated text rather than on raw figures, so the view models stay as they
 * are and every number keeps its own format: its decimals, its thousands grouping, its sign, and the
 * locale's separators. Two texts count into each other only when everything around their numbers is
 * the same ("23,7 Tage" into "22,9 Tage"); when the words differ ("< 1 %" into "5 %") the caller
 * swaps instead, since there is no in-between to show.
 */

/** A text split into the parts around its numbers and the numbers themselves. */
interface TextPart {
  text: string;
}
interface NumberPart {
  value: number;
  decimals: number;
  grouped: boolean;
  /** How the sign was written: "+" / "±" means the text shows it explicitly, "" means only a minus. */
  explicitSign: boolean;
  minus: string;
}
type Part = TextPart | NumberPart;

const isNumber = (part: Part): part is NumberPart => (part as NumberPart).value !== undefined;
const escape = (value: string) => value.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");

/** The decimal and grouping separators of a locale, read from Intl rather than assumed. */
export function separators(locale: string): { decimal: string; group: string } {
  const parts = new Intl.NumberFormat(locale).formatToParts(12345.6);
  return {
    decimal: parts.find((part) => part.type === "decimal")?.value ?? ".",
    group: parts.find((part) => part.type === "group")?.value ?? ",",
  };
}

/**
 * Splits a text into its numbers and the text around them. A number may carry a sign (+, −, -, ±)
 * directly before it, thousands groups and decimals in the locale's separators.
 */
export function splitNumbers(text: string, locale: string): Part[] {
  const { decimal, group } = separators(locale);
  const pattern = new RegExp(
    `([+\\u2212\\-\\u00b1]?)(\\d{1,3}(?:${escape(group)}\\d{3})+|\\d+)(?:${escape(decimal)}(\\d+))?`,
    "g"
  );

  const parts: Part[] = [];
  let last = 0;
  let match: RegExpExecArray | null;

  while ((match = pattern.exec(text)) !== null) {
    if (match.index > last) parts.push({ text: text.slice(last, match.index) });

    const [, sign, whole, fraction] = match;
    const magnitude = Number(`${whole.split(group).join("")}.${fraction ?? "0"}`);
    const negative = sign === "-" || sign === "−";
    parts.push({
      value: negative ? -magnitude : magnitude,
      decimals: fraction ? fraction.length : 0,
      grouped: whole.includes(group),
      explicitSign: sign === "+" || sign === "±",
      minus: negative ? sign : "−",
    });
    last = match.index + match[0].length;
  }

  if (last < text.length) parts.push({ text: text.slice(last) });
  return parts;
}

/** Whether two texts differ only in their numbers, so one can count into the other. */
export function countable(from: string, to: string, locale: string): boolean {
  const a = splitNumbers(from, locale);
  const b = splitNumbers(to, locale);

  return (
    a.length === b.length &&
    b.some(isNumber) &&
    a.every((part, index) => isNumber(part) === isNumber(b[index]) && (isNumber(part) || part.text === (b[index] as TextPart).text))
  );
}

function formatNumber(value: number, target: NumberPart, locale: string): string {
  const magnitude = new Intl.NumberFormat(locale, {
    minimumFractionDigits: target.decimals,
    maximumFractionDigits: target.decimals,
    useGrouping: target.grouped,
  }).format(Math.abs(value));
  const rounded = Number(Math.abs(value).toFixed(target.decimals));

  if (rounded === 0) return target.explicitSign ? `±${magnitude}` : magnitude;
  if (value < 0) return `${target.minus}${magnitude}`;
  return target.explicitSign ? `+${magnitude}` : magnitude;
}

/**
 * The text `progress` of the way (0 to 1) from `from` to `to`: every number moved that far, written
 * the way the target writes it. Returns `to` when the texts cannot count into each other.
 */
export function interpolateText(from: string, to: string, progress: number, locale: string): string {
  if (progress >= 1 || !countable(from, to, locale)) return to;

  const a = splitNumbers(from, locale);
  const b = splitNumbers(to, locale);

  return b
    .map((part, index) => {
      if (!isNumber(part)) return part.text;
      const start = (a[index] as NumberPart).value;
      return formatNumber(start + (part.value - start) * progress, part, locale);
    })
    .join("");
}

/** Ease-out, so the count slows as it lands: most of the change is visible at once. */
export function easeOutCubic(progress: number): number {
  return 1 - (1 - Math.min(1, Math.max(0, progress))) ** 3;
}
